/**
 * Task store - manages individual task state with optimistic updates
 *
 * Supports offline-first operations:
 * - Saves tasks to IndexedDB when offline
 * - Queues operations for background sync
 * - Merges local and remote data on reconnection
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  tasksApi,
  subtasksApi,
  labelsApi,
  type Task,
  type TaskDetail,
  type Subtask,
  type Label,
  type TaskDependency,
  type CreateTaskRequest,
  type UpdateTaskRequest,
} from '@services/api';
import { useBoardStore } from './boardStore';
import {
  saveTaskOffline,
  getTaskOffline,
  getTasksByBoard,
  type OfflineTask,
} from '@services/offline/storage';
import { addToSyncQueue } from '@services/offline/syncQueue';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  return fallback;
};

// Generate a temporary ID for offline tasks
function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Sync subtask progress counts to the board store so the task card stays fresh
function syncSubtaskCounts(taskId: string, subtasks: Subtask[]): void {
  useBoardStore.getState().updateTask(taskId, {
    subtaskCount: subtasks.length,
    subtaskCompletedCount: subtasks.filter((s) => s.isCompleted).length,
  });
}

// Closing or opening another detail invalidates pending responses.
let taskFetchSequence = 0;

interface TaskState {
  // State
  currentTask: TaskDetail | null;
  subtasks: Subtask[];
  labels: Label[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  isOffline: boolean;
  offlineTasks: OfflineTask[];

  // Task actions
  fetchTask: (taskId: string) => Promise<void>;
  fetchTaskByKey: (taskKey: string) => Promise<void>;
  createTask: (data: CreateTaskRequest) => Promise<TaskDetail>;
  createTaskOffline: (boardId: string, data: Partial<OfflineTask>) => Promise<OfflineTask>;
  updateTask: (taskId: string, data: UpdateTaskRequest) => Promise<void>;
  updateTaskOffline: (taskId: string, data: Partial<OfflineTask>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  completeTask: (taskId: string) => Promise<void>;
  uncompleteTask: (taskId: string) => Promise<void>;
  logTime: (taskId: string, minutes: number) => Promise<void>;
  moveToProject: (
    taskId: string,
    targetBoardId: string,
    targetColumnId: string,
    targetPosition: number
  ) => Promise<{ task: TaskDetail; previousTaskKey: string; labelsMapped: number; labelsDropped: number }>;

  // Offline actions
  loadOfflineTasks: (boardId: string) => Promise<void>;
  syncOfflineTasks: () => Promise<void>;
  setOfflineStatus: (isOffline: boolean) => void;

  // Subtask actions
  fetchSubtasks: (taskId: string) => Promise<void>;
  createSubtask: (taskId: string, title: string) => Promise<Subtask>;
  updateSubtask: (taskId: string, subtaskId: string, data: Partial<Subtask>) => Promise<void>;
  deleteSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  toggleSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  reorderSubtasks: (taskId: string, subtaskIds: string[]) => Promise<void>;

  // Label actions
  fetchLabels: (projectId: string) => Promise<void>;
  createLabel: (projectId: string, data: { name: string; color: string }) => Promise<Label>;
  addLabelToTask: (taskId: string, labelId: string) => Promise<void>;
  removeLabelFromTask: (taskId: string, labelId: string) => Promise<void>;

  // Dependency actions
  addDependency: (
    taskId: string,
    blockingTaskId: string,
    blockedTaskId: string
  ) => Promise<TaskDependency>;
  removeDependency: (dependencyId: string) => Promise<void>;

  // UI actions
  setCurrentTask: (task: TaskDetail | null) => void;
  setLoading: (isLoading: boolean) => void;
  setSaving: (isSaving: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

export const useTaskStore = create<TaskState>()(
  immer((set, get) => ({
    // Initial state
    currentTask: null,
    subtasks: [],
    labels: [],
    isLoading: false,
    isSaving: false,
    error: null,
    isOffline: !navigator.onLine,
    offlineTasks: [],

    // Task actions
    fetchTask: async (taskId) => {
      const sequence = ++taskFetchSequence;
      set((state) => {
        state.currentTask = null;
        state.subtasks = [];
        state.isLoading = true;
        state.error = null;
      });

      try {
        // Try online first
        if (navigator.onLine) {
          const task = await tasksApi.get(taskId);
          if (sequence !== taskFetchSequence) return;
          set((state) => {
            state.currentTask = task;
            // Spread to create mutable copy (API response may be frozen)
            state.subtasks = [...(task.subtasks || [])];
            state.isLoading = false;
          });
        } else {
          // Fallback to offline storage
          const offlineTask = await getTaskOffline(taskId);
          if (sequence !== taskFetchSequence) return;
          if (offlineTask) {
            // Create minimal TaskDetail from OfflineTask
            set((state) => {
              state.currentTask = {
                id: offlineTask.id,
                key: offlineTask.id, // Use ID as key for offline tasks
                taskKey: offlineTask.id,
                boardId: offlineTask.boardId,
                columnId: '',
                title: offlineTask.title,
                description: offlineTask.description ?? '',
                status: offlineTask.status,
                priority: (offlineTask.priority || 'medium') as TaskDetail['priority'],
                position: offlineTask.position,
                dueDate: offlineTask.dueDate,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                labels: [],
                subtasks: [],
                dependencies: [],
                comments: [],
                attachments: [],
                customFieldValues: [],
                isCompleted: offlineTask.status === 'done',
                createdById: '',
                sprintId: null,
              } as unknown as TaskDetail;
              state.subtasks = [];
              state.isLoading = false;
            });
          } else {
            set((state) => {
              state.error = 'Task not found offline';
              state.isLoading = false;
            });
          }
        }
      } catch (error: unknown) {
        if (sequence !== taskFetchSequence) return;
        // Try offline fallback on network error. Guard against IndexedDB read
        // failures so the catch below still clears isLoading instead of rejecting.
        const offlineTask = await getTaskOffline(taskId).catch(() => undefined);
        if (sequence !== taskFetchSequence) return;
        if (offlineTask) {
          set((state) => {
            state.currentTask = {
              id: offlineTask.id,
              key: offlineTask.id,
              taskKey: offlineTask.id,
              boardId: offlineTask.boardId,
              columnId: '',
              title: offlineTask.title,
              description: offlineTask.description ?? '',
              status: offlineTask.status,
              priority: (offlineTask.priority || 'medium') as TaskDetail['priority'],
              position: offlineTask.position,
              dueDate: offlineTask.dueDate,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              labels: [],
              subtasks: [],
              dependencies: [],
              comments: [],
              attachments: [],
              customFieldValues: [],
              isCompleted: offlineTask.status === 'done',
              createdById: '',
              sprintId: null,
            } as unknown as TaskDetail;
            state.subtasks = [];
            state.isLoading = false;
            state.isOffline = true;
          });
        } else {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch task';
          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
          throw error;
        }
      }
    },

    fetchTaskByKey: async (taskKey) => {
      const sequence = ++taskFetchSequence;
      set((state) => {
        state.currentTask = null;
        state.subtasks = [];
        state.isLoading = true;
        state.error = null;
      });

      try {
        const task = await tasksApi.getByKey(taskKey);
        if (sequence !== taskFetchSequence) return;
        set((state) => {
          state.currentTask = task;
          // Spread to create mutable copy (API response may be frozen)
          state.subtasks = [...(task.subtasks || [])];
          state.isLoading = false;
        });
      } catch (error: unknown) {
        if (sequence !== taskFetchSequence) return;
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch task');
          state.isLoading = false;
        });
        throw error;
      }
    },

    createTask: async (data) => {
      set((state) => {
        state.isSaving = true;
        state.error = null;
      });

      try {
        const task = await tasksApi.create(data);
        // Fetch full task details
        const taskDetail = await tasksApi.get(task.id);
        set((state) => {
          state.currentTask = taskDetail;
          // Spread to create mutable copy (API response may be frozen)
          state.subtasks = [...(taskDetail.subtasks || [])];
          state.isSaving = false;
        });

        // Sync to board store so board view reflects the new task
        // Build task object with required fields, then add optional fields conditionally
        // to satisfy exactOptionalPropertyTypes
        const boardTask: Task = {
          id: taskDetail.id,
          taskKey: taskDetail.taskKey,
          taskNumber: taskDetail.taskNumber,
          boardId: taskDetail.boardId,
          columnId: taskDetail.columnId,
          title: taskDetail.title,
          taskType: taskDetail.taskType,
          priority: taskDetail.priority,
          position: taskDetail.position,
          labels: taskDetail.labels,
          isCompleted: taskDetail.isCompleted,
          actualMinutes: taskDetail.actualMinutes,
          subtaskCount: taskDetail.subtasks?.length || 0,
          subtaskCompletedCount: taskDetail.subtasks?.filter((s) => s.isCompleted).length || 0,
          attachmentCount: taskDetail.attachmentCount || 0,
          createdAt: taskDetail.createdAt,
          updatedAt: taskDetail.updatedAt,
        };
        if (taskDetail.description) boardTask.description = taskDetail.description;
        if (taskDetail.dueDate) boardTask.dueDate = taskDetail.dueDate;
        if (taskDetail.startDate) boardTask.startDate = taskDetail.startDate;
        if (taskDetail.assigneeId) boardTask.assigneeId = taskDetail.assigneeId;
        if (taskDetail.assignee) boardTask.assignee = taskDetail.assignee;
        if (taskDetail.completedAt) boardTask.completedAt = taskDetail.completedAt;
        useBoardStore.getState().addTask(boardTask);

        return taskDetail;
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to create task');
          state.isSaving = false;
        });
        throw error;
      }
    },

    updateTask: async (taskId, data) => {
      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        state.isSaving = true;
        if (state.currentTask?.id === taskId) {
          Object.assign(state.currentTask, data);
        }
      });

      try {
        const updated = await tasksApi.update(taskId, data);
        set((state) => {
          if (state.currentTask?.id === taskId) {
            Object.assign(state.currentTask, updated);
          }
          state.isSaving = false;
        });

        // Sync to board store so board view reflects changes
        useBoardStore.getState().updateTask(taskId, updated);
      } catch (error: unknown) {
        // Rollback
        set((state) => {
          if (original && state.currentTask?.id === taskId) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to update task');
          state.isSaving = false;
        });
        throw error;
      }
    },

    deleteTask: async (taskId) => {
      set((state) => {
        state.isSaving = true;
      });

      try {
        await tasksApi.delete(taskId);
        set((state) => {
          if (state.currentTask?.id === taskId) {
            state.currentTask = null;
            state.subtasks = [];
          }
          state.isSaving = false;
        });

        // Sync to board store so board view reflects the deletion
        useBoardStore.getState().removeTask(taskId);
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to delete task');
          state.isSaving = false;
        });
        throw error;
      }
    },

    completeTask: async (taskId) => {
      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        if (state.currentTask?.id === taskId) {
          state.currentTask.isCompleted = true;
          state.currentTask.completedAt = new Date().toISOString();
        }
      });

      try {
        const updated = await tasksApi.complete(taskId);
        set((state) => {
          if (state.currentTask?.id === taskId) {
            state.currentTask.isCompleted = updated.isCompleted;
            if (updated.completedAt) {
              state.currentTask.completedAt = updated.completedAt;
            }
          }
        });

        // Sync to board store
        const boardUpdate: { isCompleted: boolean; completedAt?: string } = {
          isCompleted: updated.isCompleted,
        };
        if (updated.completedAt) {
          boardUpdate.completedAt = updated.completedAt;
        }
        useBoardStore.getState().updateTask(taskId, boardUpdate);
      } catch (error: unknown) {
        set((state) => {
          if (original && state.currentTask?.id === original.id) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to complete task');
        });
        throw error;
      }
    },

    uncompleteTask: async (taskId) => {
      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        if (state.currentTask?.id === taskId) {
          state.currentTask.isCompleted = false;
          delete (state.currentTask as { completedAt?: string }).completedAt;
        }
      });

      try {
        const updated = await tasksApi.uncomplete(taskId);
        set((state) => {
          if (state.currentTask?.id === taskId) {
            state.currentTask.isCompleted = updated.isCompleted;
            if (updated.completedAt) {
              state.currentTask.completedAt = updated.completedAt;
            } else {
              delete (state.currentTask as { completedAt?: string }).completedAt;
            }
          }
        });

        // Sync to board store
        const boardUpdate: { isCompleted: boolean; completedAt?: string } = {
          isCompleted: updated.isCompleted,
        };
        if (updated.completedAt) {
          boardUpdate.completedAt = updated.completedAt;
        }
        useBoardStore.getState().updateTask(taskId, boardUpdate);
      } catch (error: unknown) {
        set((state) => {
          if (original && state.currentTask?.id === original.id) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to uncomplete task');
        });
        throw error;
      }
    },

    logTime: async (taskId, minutes) => {
      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        if (state.currentTask?.id === taskId) {
          state.currentTask.actualMinutes =
            (state.currentTask.actualMinutes || 0) + minutes;
        }
      });

      try {
        const updated = await tasksApi.logTime(taskId, minutes);
        set((state) => {
          if (state.currentTask?.id === taskId) {
            state.currentTask.actualMinutes = updated.actualMinutes;
          }
        });
      } catch (error: unknown) {
        set((state) => {
          if (original && state.currentTask?.id === original.id) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to log time');
        });
        throw error;
      }
    },

    moveToProject: async (taskId, targetBoardId, targetColumnId, targetPosition) => {
      set((state) => {
        state.isSaving = true;
        state.error = null;
      });

      try {
        const result = await tasksApi.moveToProject(taskId, {
          targetBoardId,
          targetColumnId,
          targetPosition,
        });

        // Fetch full task details for the moved task
        const taskDetail = await tasksApi.get(result.task.id);

        // Update current task if it was the moved task
        set((state) => {
          if (state.currentTask?.id === taskId) {
            state.currentTask = taskDetail;
            state.subtasks = [...(taskDetail.subtasks || [])];
          }
          state.isSaving = false;
        });

        // Remove task from source board in board store (if viewing that board)
        const boardStore = useBoardStore.getState();
        if (boardStore.currentBoard?.id === result.previousBoardId) {
          boardStore.removeTask(taskId);
        }

        // If viewing target board, refresh to get the new task
        if (boardStore.currentBoard?.id === targetBoardId) {
          await boardStore.fetchBoardTasks(targetBoardId);
        }

        return {
          task: taskDetail,
          previousTaskKey: result.previousTaskKey,
          labelsMapped: result.labelsMapped,
          labelsDropped: result.labelsDropped,
        };
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to move task to project');
          state.isSaving = false;
        });
        throw error;
      }
    },

    // Offline-specific actions
    createTaskOffline: async (boardId, data) => {
      const taskId = generateTempId();
      const offlineTask: OfflineTask = {
        id: taskId,
        boardId,
        title: data.title || 'Untitled Task',
        priority: data.priority || 'medium',
        status: data.status || 'todo',
        position: data.position || 0,
        isOffline: true,
        localUpdatedAt: Date.now(),
      };

      // Only set optional fields if they have values
      if (data.description) {
        offlineTask.description = data.description;
      }
      if (data.dueDate) {
        offlineTask.dueDate = data.dueDate;
      }

      // Save to IndexedDB
      await saveTaskOffline(offlineTask);

      // Build sync queue payload
      const syncPayload: Record<string, unknown> = {
        boardId,
        title: offlineTask.title,
        priority: offlineTask.priority,
        status: offlineTask.status,
      };
      if (offlineTask.description) {
        syncPayload.description = offlineTask.description;
      }
      if (offlineTask.dueDate) {
        syncPayload.dueDate = offlineTask.dueDate;
      }

      // Add to sync queue
      await addToSyncQueue('create', 'task', taskId, syncPayload);

      // Update local state
      set((state) => {
        state.offlineTasks.push(offlineTask);
      });

      return offlineTask;
    },

    updateTaskOffline: async (taskId, data) => {
      // Get existing task
      const existingTask = await getTaskOffline(taskId);
      if (!existingTask) {
        throw new Error('Task not found');
      }

      const updatedTask: OfflineTask = {
        ...existingTask,
        ...data,
        localUpdatedAt: Date.now(),
      };

      // Save to IndexedDB
      await saveTaskOffline(updatedTask);

      // Add to sync queue if this isn't a temp task or if it's already synced
      if (!taskId.startsWith('temp-')) {
        await addToSyncQueue('update', 'task', taskId, data);
      }

      // Update local state
      set((state) => {
        const index = state.offlineTasks.findIndex((t) => t.id === taskId);
        if (index !== -1) {
          state.offlineTasks[index] = updatedTask;
        }
        if (state.currentTask?.id === taskId) {
          Object.assign(state.currentTask, data);
        }
      });
    },

    loadOfflineTasks: async (boardId) => {
      try {
        const tasks = await getTasksByBoard(boardId);
        set((state) => {
          // Spread to create mutable copy
          state.offlineTasks = [...tasks];
        });
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load offline tasks';
        set((state) => {
          state.error = errorMessage;
        });
      }
    },

    syncOfflineTasks: async () => {
      // This is a placeholder - actual sync is handled by the sync queue
      // This method can be used to trigger manual sync or handle post-sync cleanup
      const offlineTasks = get().offlineTasks;

      // Filter out tasks that have been synced
      const stillOffline = offlineTasks.filter((t) => t.isOffline);

      set((state) => {
        state.offlineTasks = stillOffline;
      });
    },

    setOfflineStatus: (isOffline) => {
      set((state) => {
        state.isOffline = isOffline;
      });
    },

    // Subtask actions
    fetchSubtasks: async (taskId) => {
      try {
        const subtasks = await subtasksApi.list(taskId);
        set((state) => {
          // Spread to create mutable copy (API response may be frozen)
          state.subtasks = [...subtasks];
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch subtasks');
        });
        throw error;
      }
    },

    createSubtask: async (taskId, title) => {
      try {
        const subtask = await subtasksApi.create(taskId, { title });
        set((state) => {
          state.subtasks.push(subtask);
          if (state.currentTask?.id === taskId) {
            state.currentTask.subtasks = [...(state.currentTask.subtasks || []), subtask];
          }
        });
        // Keep the board card's progress indicator in sync
        syncSubtaskCounts(taskId, get().subtasks);
        return subtask;
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to create subtask');
        });
        throw error;
      }
    },

    updateSubtask: async (taskId, subtaskId, data) => {
      const originalSubtasks = [...get().subtasks];

      // Optimistic update
      set((state) => {
        const index = state.subtasks.findIndex((s: Subtask) => s.id === subtaskId);
        if (index !== -1) {
          const subtask = state.subtasks[index];
          if (subtask) {
            Object.assign(subtask, data);
          }
        }
      });

      try {
        const updated = await subtasksApi.update(taskId, subtaskId, data);
        set((state) => {
          const index = state.subtasks.findIndex((s: Subtask) => s.id === subtaskId);
          if (index !== -1) {
            state.subtasks[index] = updated;
          }
        });
        // Keep the board card's progress indicator in sync
        syncSubtaskCounts(taskId, get().subtasks);
      } catch (error: unknown) {
        set((state) => {
          if (state.currentTask?.id === taskId) state.subtasks = originalSubtasks;
          state.error = getErrorMessage(error, 'Failed to update subtask');
        });
        throw error;
      }
    },

    deleteSubtask: async (taskId, subtaskId) => {
      const originalSubtasks = [...get().subtasks];

      // Optimistic update
      set((state) => {
        state.subtasks = state.subtasks.filter((s: Subtask) => s.id !== subtaskId);
      });

      try {
        await subtasksApi.delete(taskId, subtaskId);
        // Keep the board card's progress indicator in sync
        syncSubtaskCounts(taskId, get().subtasks);
      } catch (error: unknown) {
        set((state) => {
          if (state.currentTask?.id === taskId) state.subtasks = originalSubtasks;
          state.error = getErrorMessage(error, 'Failed to delete subtask');
        });
        throw error;
      }
    },

    toggleSubtask: async (taskId, subtaskId) => {
      const subtask = get().subtasks.find((s) => s.id === subtaskId);
      if (!subtask) return;

      // Capture the current state before optimistic update
      const wasCompleted = subtask.isCompleted;
      const originalSubtasks = [...get().subtasks];

      // Optimistic update
      set((state) => {
        const index = state.subtasks.findIndex((s: Subtask) => s.id === subtaskId);
        if (index !== -1 && state.subtasks[index]) {
          state.subtasks[index].isCompleted = !state.subtasks[index].isCompleted;
        }
      });

      try {
        if (wasCompleted) {
          await subtasksApi.uncomplete(taskId, subtaskId);
        } else {
          await subtasksApi.complete(taskId, subtaskId);
        }
        // Keep the board card's progress indicator in sync
        syncSubtaskCounts(taskId, get().subtasks);
      } catch (error: unknown) {
        set((state) => {
          if (state.currentTask?.id === taskId) state.subtasks = originalSubtasks;
          state.error = getErrorMessage(error, 'Failed to toggle subtask');
        });
        throw error;
      }
    },

    reorderSubtasks: async (taskId, subtaskIds) => {
      const originalSubtasks = [...get().subtasks];

      // Optimistic update
      set((state) => {
        state.subtasks.sort((a: Subtask, b: Subtask) => {
          return subtaskIds.indexOf(a.id) - subtaskIds.indexOf(b.id);
        });
        state.subtasks.forEach((s: Subtask, idx: number) => {
          s.position = idx;
        });
      });

      try {
        await subtasksApi.reorder(taskId, subtaskIds);
      } catch (error: unknown) {
        set((state) => {
          if (state.currentTask?.id === taskId) state.subtasks = originalSubtasks;
          state.error = getErrorMessage(error, 'Failed to reorder subtasks');
        });
        throw error;
      }
    },

    // Label actions
    fetchLabels: async (projectId) => {
      try {
        const labels = await labelsApi.list(projectId);
        set((state) => {
          // Spread to create mutable copy (API response may be frozen)
          state.labels = [...labels];
        });
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to fetch labels');
        });
        throw error;
      }
    },

    createLabel: async (projectId, data) => {
      try {
        const label = await labelsApi.create({ projectId, ...data });
        set((state) => {
          state.labels.push(label);
        });
        return label;
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to create label');
        });
        throw error;
      }
    },

    addLabelToTask: async (taskId, labelId) => {
      const label = get().labels.find((l: Label) => l.id === labelId);
      if (!label) return;

      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        if (state.currentTask?.id === taskId) {
          if (!state.currentTask.labels.some((l: Label) => l.id === labelId)) {
            state.currentTask.labels.push(label);
          }
        }
      });

      try {
        await tasksApi.addLabel(taskId, labelId);

        // Sync to board store — guard against navigation to another task while
        // the request was in flight, which would push the wrong labels.
        const currentTask = get().currentTask;
        if (currentTask && currentTask.id === taskId) {
          useBoardStore.getState().updateTask(taskId, { labels: currentTask.labels });
        }
      } catch (error: unknown) {
        set((state) => {
          if (original && state.currentTask?.id === original.id) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to add label');
        });
        throw error;
      }
    },

    removeLabelFromTask: async (taskId, labelId) => {
      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        if (state.currentTask?.id === taskId) {
          state.currentTask.labels = state.currentTask.labels.filter(
            (l: Label) => l.id !== labelId
          );
        }
      });

      try {
        await tasksApi.removeLabel(taskId, labelId);

        // Sync to board store — guard against navigation to another task while
        // the request was in flight, which would push the wrong labels.
        const currentTask = get().currentTask;
        if (currentTask && currentTask.id === taskId) {
          useBoardStore.getState().updateTask(taskId, { labels: currentTask.labels });
        }
      } catch (error: unknown) {
        set((state) => {
          if (original && state.currentTask?.id === original.id) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to remove label');
        });
        throw error;
      }
    },

    // Dependency actions
    addDependency: async (taskId, blockingTaskId, blockedTaskId) => {
      try {
        const dependency = await tasksApi.addDependency(taskId, {
          blockingTaskId,
          blockedTaskId,
        });
        set((state) => {
          if (state.currentTask?.id === taskId) {
            state.currentTask.dependencies.push(dependency);
          }
        });
        return dependency;
      } catch (error: unknown) {
        set((state) => {
          state.error = getErrorMessage(error, 'Failed to add dependency');
        });
        throw error;
      }
    },

    removeDependency: async (dependencyId) => {
      const original = get().currentTask;

      // Optimistic update
      set((state) => {
        if (state.currentTask) {
          state.currentTask.dependencies = state.currentTask.dependencies.filter(
            (d: TaskDependency) => d.id !== dependencyId
          );
        }
      });

      try {
        await tasksApi.removeDependency(dependencyId);
      } catch (error: unknown) {
        set((state) => {
          if (original && state.currentTask?.id === original.id) {
            state.currentTask = original;
          }
          state.error = getErrorMessage(error, 'Failed to remove dependency');
        });
        throw error;
      }
    },

    // UI actions
    setCurrentTask: (task) =>
      set((state) => {
        state.currentTask = task;
        // Spread to create mutable copy (API response may be frozen)
        state.subtasks = [...(task?.subtasks || [])];
      }),

    setLoading: (isLoading) =>
      set((state) => {
        state.isLoading = isLoading;
      }),

    setSaving: (isSaving) =>
      set((state) => {
        state.isSaving = isSaving;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
      }),

    reset: () => {
      ++taskFetchSequence;
      set((state) => {
        state.currentTask = null;
        state.subtasks = [];
        state.labels = [];
        state.isLoading = false;
        state.isSaving = false;
        state.error = null;
        state.isOffline = !navigator.onLine;
        state.offlineTasks = [];
      });
    },
  }))
);
